import { MenuIcon, PenIcon, TrashIcon } from "@xola/icons";
import { Badge, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { SecondaryButton } from "common";
import { truncate } from "lodash";
import React from "react";
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    Droppable,
    DroppableProvided,
    DropResult,
} from "react-beautiful-dnd";
import { VariantOption } from "types";

const MAX_OPTION_DISPLAY_LENGTH = 30;

const OptionBadge = ({ name }: { name: string }) => {
    const displayName = truncate(name, {
        length: MAX_OPTION_DISPLAY_LENGTH,
        omission: "…",
    });

    return (
        <Badge color="gray" size="small">
            {displayName}
        </Badge>
    );
};

interface VariantOptionsDndTableProps {
    options: VariantOption[];
    onDragEnd: (result: DropResult) => void;
    onEditOption: (option: VariantOption, index: number) => void;
    onDeleteOption: (option: VariantOption, index: number) => void;
}

export const VariantOptionsDndTable = ({
    options,
    onDragEnd,
    onEditOption,
    onDeleteOption,
}: VariantOptionsDndTableProps) => {
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable
                droppableId="variantOptionsTable"
                renderClone={(provided, _, rubric) => (
                    <VariantOptionCloneRow provided={provided} option={options[rubric.source.index]} />
                )}
            >
                {(provided: DroppableProvided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        <Table>
                            <Table.Head>
                                <Table.Row>
                                    <Table.Header className="w-8" />
                                    <Table.Header>Option</Table.Header>
                                    <Table.Header className="w-24 text-right">Actions</Table.Header>
                                </Table.Row>
                            </Table.Head>
                            <Table.Body isStriped={false}>
                                {options.map((option, index) => (
                                    <Draggable
                                        key={option.id ?? `${option.name}-${index}`}
                                        draggableId={option.id ?? `${option.name}-${index}`}
                                        index={index}
                                    >
                                        {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
                                            <tr
                                                ref={innerRef}
                                                {...draggableProps}
                                                {...dragHandleProps}
                                                className={clsx("justify-between", {
                                                    "bg-white shadow": snapshot.isDragging,
                                                })}
                                            >
                                                <Table.Cell className="w-8">
                                                    <span className="inline-flex cursor-grab rounded border border-gray-light px-2 py-0.5">
                                                        <MenuIcon size="small" />
                                                    </span>
                                                </Table.Cell>
                                                <Table.Cell data-testid="option-name">
                                                    <OptionBadge name={option.name} />
                                                </Table.Cell>
                                                <Table.Cell className="w-24 space-x-1 text-right">
                                                    <SecondaryButton
                                                        size="small"
                                                        type="button"
                                                        data-testid="option-edit-button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onEditOption(option, index);
                                                        }}
                                                    >
                                                        <PenIcon size="small" />
                                                    </SecondaryButton>
                                                    <SecondaryButton
                                                        size="small"
                                                        type="button"
                                                        data-testid="option-remove-button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onDeleteOption(option, index);
                                                        }}
                                                    >
                                                        <TrashIcon size="small" />
                                                    </SecondaryButton>
                                                </Table.Cell>
                                            </tr>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </Table.Body>
                        </Table>
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

interface VariantOptionCloneRowProps {
    provided: DraggableProvided;
    option: VariantOption;
}

const VariantOptionCloneRow = ({ provided, option }: VariantOptionCloneRowProps) => (
    <table
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className="w-full bg-white shadow"
        style={{
            ...provided.draggableProps.style,
            tableLayout: "fixed",
        }}
    >
        <tbody>
            <tr>
                <Table.Cell className="w-12">
                    <span className="inline-flex rounded border border-gray-light px-2 py-0.5">
                        <MenuIcon size="small" />
                    </span>
                </Table.Cell>
                <Table.Cell>
                    <OptionBadge name={option.name} />
                </Table.Cell>
                <Table.Cell className="w-24 space-x-1 text-right">
                    <SecondaryButton size="small" type="button">
                        <PenIcon size="small" />
                    </SecondaryButton>
                    <SecondaryButton size="small" type="button">
                        <TrashIcon size="small" />
                    </SecondaryButton>
                </Table.Cell>
            </tr>
        </tbody>
    </table>
);
